// Weekly Mirror — one reading per week, premium.
//
// A free account still sees this screen with a teaser, because hiding it entirely makes the value
// impossible to judge. The archive is a list of past weeks; the newest opens expanded.

import { useEffect, useState } from 'react';
import { useDEK } from '../auth/SessionProvider';
import { fetchMirrors, type Mirror } from '../api/mirror';
import { startCheckout } from '../api/billing';
import { EmptyState, Notice, PrimaryButton, Spinner } from '../components/ui';
import { IconLock, IconSpark } from '../components/Icons';

export function MirrorScreen() {
  const dek = useDEK();
  const [isPremium, setIsPremium] = useState<boolean | null>(null);
  const [mirrors, setMirrors] = useState<Mirror[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [archiveFailed, setArchiveFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setArchiveFailed(false);
    fetchMirrors(dek, controller.signal)
      .then((archive) => {
        if (controller.signal.aborted) return;
        setIsPremium(archive.isPremium);
        setMirrors(archive.mirrors);
        setOpenId(archive.mirrors[0]?.id ?? null);
      })
      .catch(() => {
        if (!controller.signal.aborted) setArchiveFailed(true);
      });
    return () => controller.abort();
  }, [dek, attempt]);

  async function upgrade() {
    setBusy(true);
    try {
      window.location.href = await startCheckout();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open checkout.');
      setBusy(false);
    }
  }

  return (
    <>
      <div className="ohead">
        <div className="obrand">
          <span className="nm" style={{ fontSize: 22 }}>Mirror</span>
        </div>
        <div className="olock">
          <IconLock /> Private
        </div>
      </div>

      <div className="ojhead">
        <h1>
          Your week,
          <br />
          <em className="oitalic">read back to you.</em>
        </h1>
        <p className="ojsub">
          After each week closes, Oreia reads what you wrote and reflects what kept returning —
          including the contradictions.
        </p>
      </div>

      {archiveFailed ? (
        <EmptyState title="Your archive couldn't be loaded." body="Please try again. Your subscription has not changed."
          action={<button className="obtn" onClick={() => setAttempt((n) => n + 1)}>Try again</button>} />
      ) : isPremium === null ? (
        <Spinner label="Opening your archive…" />
      ) : mirrors.length === 0 ? (
        isPremium ? (
          <EmptyState
            title="Your first mirror is still forming."
            body="Write at least two entries during a Monday–Sunday week. Your mirror is prepared after the week closes at midnight UTC on Monday. Drafts and historical entries do not count."
          />
        ) : (
          <div className="ocard ocard-bright">
            <span className="olabel" style={{ color: 'var(--accent-1)' }}>Premium</span>
            <h3 className="oserif" style={{ fontSize: 25, margin: '12px 0 10px', lineHeight: 1.2 }}>
              Ten sections, grounded in what you actually wrote.
            </h3>
            <p className="obody">
              The emotional weather of the week. What kept returning. Where your dreams and waking
              life disagreed. The question worth sitting with. And one experiment for the week ahead.
            </p>
            {error ? (
              <div style={{ marginTop: 14 }}>
                <Notice tone="danger">{error}</Notice>
              </div>
            ) : null}
            <div style={{ marginTop: 18 }}>
              <PrimaryButton onClick={upgrade} loading={busy}>
                <IconSpark /> Unlock Weekly Mirror
              </PrimaryButton>
            </div>
          </div>
        )
      ) : (
        <div className="ostack" style={{ gap: 12 }}>
          {mirrors.map((mirror) => (
            <MirrorCard
              key={mirror.id}
              mirror={mirror}
              open={openId === mirror.id}
              onToggle={() => setOpenId(openId === mirror.id ? null : mirror.id)}
            />
          ))}
        </div>
      )}

      <div style={{ height: 60 }} />
    </>
  );
}

function MirrorCard({
  mirror,
  open,
  onToggle,
}: {
  mirror: Mirror;
  open: boolean;
  onToggle(): void;
}) {
  const s = mirror.sections;

  return (
    <article className="ocard ocard-light">
      <button
        className="olink-plain"
        onClick={onToggle}
        aria-expanded={open}
        style={{ width: '100%', textAlign: 'left' }}
      >
        <div className="ohead-row">
          <span className="oserif" style={{ fontSize: 21, letterSpacing: '-0.01em' }}>
            {weekLabel(mirror.weekStart, mirror.weekEnd)}
          </span>
          <span className="omono">
            {mirror.entryCount} {mirror.entryCount === 1 ? 'entry' : 'entries'}
          </span>
        </div>
      </button>

      {s?.oneSentence ? (
        <p
          className="oserif oitalic"
          style={{ fontSize: 19, lineHeight: 1.45, margin: '14px 0 0', color: 'var(--ink-soft)' }}
        >
          {s.oneSentence}
        </p>
      ) : null}

      {open ? (
        <div className="ostack" style={{ gap: 20, marginTop: 22 }}>
          <Section label="Emotional weather" body={s?.emotionalWeather} />
          <ListSection label="What kept returning" items={s?.whatKeptReturning} />
          <Section label="Dream and waking" body={s?.dreamWaking} />
          <ListSection label="Archetype movement" items={s?.archetypeMovement} />
          <Section label="A contradiction" body={s?.contradiction} />
          <Section label="A question to sit with" body={s?.question} emphasis />
          <Section label="An experiment" body={s?.experiment} />
          <Section label="What may be changing" body={s?.mayBeChanging} />
          <Section label="What Oreia wants to check" body={s?.wantsToCheck} />

          {!s && mirror.report ? (
            // Older mirrors were written before the structured sections existed.
            <Section label="The week" body={mirror.report} />
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function Section({ label, body, emphasis }: { label: string; body?: string; emphasis?: boolean }) {
  if (!body?.trim()) return null;
  return (
    <div>
      <span className="olabel" style={{ display: 'block', marginBottom: 8 }}>
        {label}
      </span>
      {emphasis ? (
        <p className="oserif" style={{ fontSize: 21, lineHeight: 1.45, margin: 0 }}>
          {body}
        </p>
      ) : (
        <p className="obody" style={{ margin: 0 }}>
          {body}
        </p>
      )}
    </div>
  );
}

function ListSection({ label, items }: { label: string; items?: string[] }) {
  const present = (items ?? []).filter((item) => item.trim().length > 0);
  if (present.length === 0) return null;
  return (
    <div>
      <span className="olabel" style={{ display: 'block', marginBottom: 8 }}>
        {label}
      </span>
      <ul className="obody" style={{ margin: 0, paddingLeft: '1.1em' }}>
        {present.map((item, i) => (
          <li key={i} style={{ marginBottom: 6 }}>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** "Mar 3 – Mar 9" from two ISO dates, falling back to whatever the server sent. */
function weekLabel(start: string, end: string): string {
  const format = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };
  if (!start && !end) return 'This week';
  return `${format(start)} – ${format(end)}`;
}
